import React, { useState } from 'react';
import { updateResponse } from '../../api/questionnaire';
import { ConfirmationModal } from '../ui/Modal';

/**
 * EditResponseModal - Modal for editing questionnaire responses
 */
const EditResponseModal = ({ question, leadId, onClose, onSave }) => {
  const [responseText, setResponseText] = useState(question._draftText || question.response_text || '');
  const [changeReason, setChangeReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!responseText.trim()) {
      setError('Response cannot be empty');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await updateResponse(
        leadId,
        question.question_id,
        {
          response_text: responseText.trim(),
          change_reason: changeReason.trim() || 'User edit'
        }
      );

      // Backend returns {data: {...}, meta: {...}} on success
      if (response.data) {
        onSave && onSave(response.data);
      } else {
        setError(response.message || 'Failed to save response');
      }
    } catch (err) {
      console.error('Error saving response:', err);
      // questionnaire API uses raw axios, so err is an AxiosError
      const errorMessage = err.response?.data?.detail?.error?.message
        || (typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null)
        || err.message
        || 'Failed to save response';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setSaving(false);
    }
  };

  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const handleCancel = () => {
    if (responseText !== (question._draftText || question.response_text || '')) {
      setShowDiscardModal(true);
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Response - Q{question.question_id}
              </h3>
              {question.is_required && (
                <span className="text-xs text-red-600 font-medium">* Required Question</span>
              )}
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 flex-1 overflow-y-auto">
          {/* Question */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Question
            </label>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-800">{question.question_text}</p>
            </div>
          </div>

          {/* Current Response (if exists) */}
          {question.response_text && question.response_text !== responseText && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Previous Response
              </label>
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{question.response_text}</p>
              </div>
            </div>
          )}

          {/* Response Text Area */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Response *
            </label>
            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Enter your response here..."
              rows={8}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary resize-none"
            />
            <div className="mt-1 text-xs text-gray-500">
              {responseText.length} characters
            </div>
          </div>

          {/* Change Reason */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Change (Optional)
            </label>
            <input
              type="text"
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="e.g., Corrected fund size based on meeting discussion"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary"
              maxLength={500}
            />
            <div className="mt-1 text-xs text-gray-500">
              Helps track why changes were made (optional)
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !responseText.trim()}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <span className="inline-block animate-spin mr-2">⟳</span>
                Saving...
              </>
            ) : (
              'Save Response'
            )}
          </button>
        </div>
      </div>

      {/* Discard Changes Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        onConfirm={() => { setShowDiscardModal(false); onClose(); }}
        title="Discard Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmText="Discard"
        cancelText="Keep Editing"
        confirmVariant="primary"
      />
    </div>
  );
};

export default EditResponseModal;
